import {ITerritoryBaseLayer, TerritoryBaseFeatureWorker} from "@esWebAPI/es/map/layer/territory/TerritoryLayer";
import {Feature} from "geojson";
import TerritoryModel, {ITerritoryActions, ITerritoryFeature} from "../../TerritoryModel";
import {Map, source} from "azure-maps-control"
import {MapLayer} from "@esWebAPI/es/map/layer/MapLayer";
import {ILayerView, LayerInfo} from "@esWebAPI/es/model/Layer";
import {DatasourceManager} from "@esWebAPI/es/map/module/DatasourceManager";
import {IEventChannel} from "@esWebAPI/es/model/Models";
import {AzureSymbolLayer} from "@esWebAPI/es/map/layer/azure/AzureSymbolLayer";
import {AzureImageSpriteManager} from "@esWebAPI/es/map/layer/AzureImageSpriteManager";
import {ThematicStylerFactory} from "@esWebAPI/es/map/styler/ThematicStylerFactory";
import {TerritoryLayerWorkerBroker} from "@esWebAPI/es/map/layer/territory/TerritoryLayerWorkerBroker";
import {ImageSpriteGenerator} from "@esWebAPI/es/map/layer/ImageSpriteGenerator";
import {ExpressionFilterSpecification} from "@maplibre/maplibre-gl-style-spec";


export class TerritoryPointRelatedLayer extends MapLayer implements ITerritoryBaseLayer {

    private readonly _symbolLayer: AzureSymbolLayer;
    private readonly _worker: TerritoryLayerWorkerBroker;
    private readonly _territoryModel: TerritoryModel;

    private datasource: source.Source|undefined;


    constructor(map: Map, layerView: ILayerView, layerInfo: LayerInfo,
                datasourceManager: DatasourceManager,
                spriteManager: AzureImageSpriteManager,
                stylerFactory: ThematicStylerFactory,
                territoryModel: TerritoryModel,
                eventChannel: IEventChannel) {
        super(map, layerView, layerInfo, datasourceManager, eventChannel);

        this._symbolLayer = new AzureSymbolLayer(map, layerView, layerInfo, datasourceManager, spriteManager,
            stylerFactory.getColorStyler(layerView),
            stylerFactory.getSizeStyler(),
            stylerFactory.getSymbolStyler(),
            undefined, new ImageSpriteGenerator(spriteManager, territoryModel));

        this._territoryModel = territoryModel;
        this._worker = new TerritoryLayerWorkerBroker(territoryModel);

        territoryModel.eventChannel.addListener("onTerritoryChange", this.onTerritoryChange, this);
        territoryModel.eventChannel.addListener("onTerritoryFilterChange", this.onTerritoryFilterChange, this);
        territoryModel.eventChannel.addListener("onTerritoryLevelChange", this.onTerritoryLevelChange, this);

        this.createLayer().then(() => {
            this._onReady.resolve();
        }).catch((e) => {
            console.error("Error creating TerritoryPointRelatedLayer", e);
            this._onReady.reject(e);
        });
    }

    private async createLayer() {
        this.datasource = await this.datasourceManager.create(this.layerView, this.layerInfo, false);
        await this._symbolLayer.create(this.datasource);
        await this._worker.create(this.layerView, {});
    }

    private async recreate() {
        if (!this.datasource) {
            return;
        }
        this._symbolLayer.remove();
        this.datasource = await this.datasourceManager.recreate(this.datasource.getId(), this.layerView, this.layerInfo)
        await this._symbolLayer.recreate(this.layerView, this.layerInfo, this.datasource);
    }

    private async onTerritoryChange(actions: ITerritoryActions, _territories: ITerritoryFeature[], _changeTerritoryIds: number[]) {
        if (actions.RELOAD || actions.UNDO || actions.REDO) {
            return this.recreate();
        }
        await this._symbolLayer.update(this.layerView, this.layerInfo);
    }

    private async onTerritoryFilterChange() {
        return this.recreate();
    }

    private async onTerritoryLevelChange() {
        await this._symbolLayer.update(this.layerView, this.layerInfo)
    }

    public createLabel(feature: Feature): string {
        const labelAttrs = this.layerView.styleSettings.labelSettings.labelAttrs;
        return feature.properties?.[labelAttrs[0]] ?? "";
    }

    public get featureWorker(): TerritoryBaseFeatureWorker {
        return this._worker;
    }

    public get territoryModel(): TerritoryModel {
        return this._territoryModel;
    }

    public getLayerIds(): string[] {
        return [this._symbolLayer.getLayerId()];
    }

    public destroy(): void {
        super.destroy();
        this._symbolLayer.remove();
        if (this.datasource) {
            this.datasourceManager.remove(this.datasource.getId());
        }
        this.territoryModel.eventChannel.removeListener("onTerritoryChange", this.onTerritoryChange);
        this.territoryModel.eventChannel.removeListener("onTerritoryFilterChange", this.onTerritoryFilterChange);
        this.territoryModel.eventChannel.removeListener("onTerritoryLevelChange", this.onTerritoryLevelChange);
        this._worker.destroy();
    }

    protected getColorThematicFilter(): ExpressionFilterSpecification | undefined {
        return undefined;
    }

    protected getSymbolThematicFilter(): ExpressionFilterSpecification | undefined {
        return undefined;
    }

    protected hideFeature(_featureId: number): void {}

    protected showFeatures(): void {}

    public update(): Promise<void> {
        return this._symbolLayer.update(this.layerView, this.layerInfo);
    }

    public getActiveAzureLayerId(): string | undefined {
        return this._symbolLayer.getLayerId();
    }

    public show(): void {
        this._symbolLayer.show();
    }
    public hide(): void {
        this._symbolLayer.hide();
    }
}